import { useCallback, useState, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { Box, Paper } from '@mui/material';
import {
  GridCallbackDetails,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  DataGridHeader,
  DataGridTitle,
  DataGridFilters,
  DataGridSearch,
} from '../../tables/DataGridPro';

import { useCanReadEscalation } from '../../../hooks/permissions/useCanReadEscalation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

import { SortModel } from '../../../data/models/sort-model.model';
import { TripStatusTodaysTripsBCList } from '../../../data/models/trip-leg-status.model';
import { TripLegEventType } from '../../../services/webSocketService/updateTripLeg';

import * as triplegSlice from '../../../data/tripleg.slice';
import * as providerSlice from '../../../data/provider.slice';

import { TodaysTripsTable } from './TodaysTripsTable';

const PREFIX = 'TodaysTrips';

const classes = {
  container: `${PREFIX}-container`,
  tableContainer: `${PREFIX}-tableContainer`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingTop: '24px',
    paddingRight: '32px',
    paddingLeft: '32px',
  },
  [`& .${classes.tableContainer}`]: {
    width: '100%',
    maxHeight: '677px',
    height: '677px',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function TodaysTrips() {
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation('common');
  const canReadEscalation = useCanReadEscalation();

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleFilterChange = (filter: string) => {
    // console.log('filter: ', filter);
  };

  const dispatch = useAppDispatch();

  const todaysTripLegs = useAppSelector(triplegSlice.selectTodaysTripLegs);
  const todaysTripLegsLoading = useAppSelector(
    triplegSlice.selectLoadingTodaysTripLegs,
  );

  const page = useAppSelector(triplegSlice.selectTodayPage);
  const pageSize = useAppSelector(triplegSlice.selectTodayPageSize);
  const sortModel = useAppSelector(triplegSlice.selectTodayPageSortModel);
  const savedProviderCallOutcome = useAppSelector(
    providerSlice.selectSavedProviderCallOutcome,
  );
  const escalatedTripLeg = useAppSelector(triplegSlice.selectEscalatedTripLeg);
  const tripLegEventEmitted = useAppSelector(
    triplegSlice.selectTripLegEventEmitted,
  );

  // Filter out rideshare trips with transportationProviderTierID === 2
  const filteredTodaysTripLegs = useMemo(() => {
    if (!todaysTripLegs) return todaysTripLegs;

    const filteredItems = todaysTripLegs.items.filter(
      (tripLeg) => tripLeg.transportationProviderTierID !== 2,
    );

    return {
      ...todaysTripLegs,
      items: filteredItems,
      itemCount: filteredItems.length,
    };
  }, [todaysTripLegs]);

  const handlePaginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== todaysTripLegs.currentPage - 1) {
      dispatch(triplegSlice.setTodayPage(model.page + 1));
    }
    if (model.pageSize !== pageSize) {
      dispatch(triplegSlice.setTodayPageSize(model.pageSize));
    }
  };

  const handleSortModelChange = (
    model: GridSortModel,
    details: GridCallbackDetails,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
    }
    dispatch(triplegSlice.setTodayPage(1));
    if (newModel) {
      dispatch(triplegSlice.setTodayPageSortModel(newModel));
    }
  };

  const fetchTripLegs = useCallback(() => {
    const today = DateTime.now().toISODate();
    dispatch(
      triplegSlice.fetchTodaysTripLegsAsync({
        page,
        pageSize,
        sortModel,
        isEscalated: canReadEscalation,
        tripLegStatusBCs: [...TripStatusTodaysTripsBCList],
        includeFindProviderCallOutcomes: true,
        appointmentStartDate: today,
        appointmentEndDate: today,
        searchText: searchTerm || undefined,
      }),
    );
  }, [dispatch, page, pageSize, sortModel, canReadEscalation, searchTerm]);

  const secondaryFilters = [
    {
      label: 'Trip Status',
      name: 'tripStatus',
      value: 'All',
      options: ['All', ...TripStatusTodaysTripsBCList],
    },
    {
      label: 'Passenger',
      name: 'passenger',
      value: 'All',
      options: ['All'],
    },
  ];

  useEffect(() => {
    fetchTripLegs();
  }, [fetchTripLegs]);

  useEffect(() => {
    if (savedProviderCallOutcome) {
      fetchTripLegs();
    }
  }, [savedProviderCallOutcome, fetchTripLegs]);

  useEffect(() => {
    if (escalatedTripLeg) {
      fetchTripLegs();
      dispatch(triplegSlice.setEscalatedTripLeg(false));
    }
  }, [escalatedTripLeg, fetchTripLegs, dispatch]);

  useEffect(() => {
    if (tripLegEventEmitted) {
      const index = filteredTodaysTripLegs?.items?.findIndex(
        (tripLeg) =>
          tripLeg?.tripLegReferenceID ===
          tripLegEventEmitted?.detail?.tripLegReferenceID,
      );
      if (index >= 0) {
        fetchTripLegs();
      }
      dispatch(triplegSlice.setTripLegEventEmitted(null));
    }
  }, [tripLegEventEmitted, fetchTripLegs, dispatch, filteredTodaysTripLegs]);

  return (
    <>
      <StyledBox elevation={2}>
        <Box className={classes.container}>
          <DataGridHeader>
            <DataGridTitle>{t('todaysTrips.todaysTrips')}</DataGridTitle>

            <DataGridFilters>
              <DataGridSearch
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                placeholder={t('todaysTrips.searchPlaceholder')}
              />
              {/* <DataGridFilterMenu
                label={t('todaysTrips.filters')}
                // primaryFilter={}
                secondaryFilters={secondaryFilters}
                onFilterChange={handleFilterChange}
              /> */}
            </DataGridFilters>
          </DataGridHeader>
          <Box className={classes.tableContainer}>
            <TodaysTripsTable
              todaysTripLegs={filteredTodaysTripLegs}
              todaysTripLegsLoading={todaysTripLegsLoading}
              page={page}
              pageSize={pageSize}
              handlePaginationChange={handlePaginationChange}
              handleSortModelChange={handleSortModelChange}
            />
          </Box>
        </Box>
      </StyledBox>
      <Box sx={{ height: 24 }} />
    </>
  );
}
